import { makeObservable, observable, runInAction, computed, action } from 'mobx'
import { DateTime } from 'luxon'
import { PublicUser } from '../models/PublicUser'

import type { StaticModelCollection } from '../firestore-mobx/model'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import {
  getCollaboratorAssistants,
  getMyAssistants,
} from '../firestore/PublicUser'
import {
  createTaInvitation,
  deleteInvitation,
  getAllAssistantInvitations,
  updateInvitationSections,
} from '../firestore/Invitation'
import {
  updateSectionCollaborators,
  batchUpdateSectionCollaborators,
} from '../firestore/Section'
import {
  createAssistantship,
  deleteAssistantship,
} from '../firestore/UserProfileInstructor'
import { SectionRequestType } from '../types'
import type {
  ImpersonationInfo,
  SectionWithStatusAndAssignments,
} from '../types'
import { Invitation } from '../models/Invitation'
import type { UserProfile } from '../models/UserProfile'
import { fetchUserProfiles } from '../firestore/UserProfile'
import { getSectionsStreamForInstructor } from '../firestore/Section'
import { Section } from '../models/Section'
import { SectionRequest } from '../models/SectionRequest'
import { SectionAssignment } from '../models/SectionAssignment'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import { getSectionRequestsBySectionId } from '../firestore/SectionRequest'
import { captureException } from '@sentry/core'
import { guardOn, guardOnLoaded } from '../util/guardOn'

export type AccessLevel = 'full' | 'limited'

/** AKA 'Assistant' */
export type LimitedAccessAssistant = {
  type: 'limited'
  user: PublicUser
  sectionIds: string[]
}
/** AKA 'Collaborator' */
export type FullAccessAssistant = {
  type: 'full'
  user: PublicUser
}

export class InstructorAssistantsCubit extends Cubit {
  repository: FirebaseRepository

  public fullAccessAssistants: StaticModelCollection<PublicUser>
  public limitedAccessAssistants = observable.array<LimitedAccessAssistant>()
  public allInvitations: StaticModelCollection<Invitation>
  public assistantProfiles = observable.map<string, UserProfile>()
  private _sections: StaticModelCollection<Section>
  public assignmentsForSection = observable.map<
    string,
    StaticModelCollection<SectionAssignment>
  >()

  public invoiceRequestsBySectionId = observable.map<
    string,
    StaticModelCollection<SectionRequest>
  >()

  impersonationInfo?: ImpersonationInfo
  assistantId?: string
  invitationId?: string

  constructor(
    repository: FirebaseRepository,
    {
      impersonationInfo,
      assistantId,
      invitationId,
    }: { impersonationInfo?: ImpersonationInfo } & (
      | {
          assistantId?: string
          invitationId?: never
        }
      | {
          assistantId?: never
          invitationId?: string
        }
    )
  ) {
    super()
    makeObservable(this)

    this.impersonationInfo = impersonationInfo
    this.assistantId = assistantId
    this.invitationId = invitationId

    this.repository = repository

    this.fullAccessAssistants = PublicUser.emptyCollection(repository)
    this.allInvitations = Invitation.emptyCollection(repository)
    this._sections = Section.emptyCollection(repository)
  }

  initialize(): void {
    this.addStream(
      getSectionsStreamForInstructor(this.repository, this.impersonationInfo),
      (sections) => {
        this._sections.replaceModels(sections)
        sections.forEach((section) => {
          this.startAssignmentStream(section)
          if (section.data.organizationId) {
            this.startSectionRequestStream(section)
          }
        })
      },
      { name: 'sections-for-assistants' }
    )

    this.addStream(
      getMyAssistants(this.repository, {
        instructorUserId: this.impersonationInfo?.userId,
      }),
      (assistants) => {
        this.fullAccessAssistants.replaceModels(assistants)
        const userIds = assistants.map((a) => a.id)
        if (userIds.length > 0) {
          fetchUserProfiles(this.repository, { userIds }).then((profiles) => {
            runInAction(() => {
              profiles.forEach((p) => this.assistantProfiles.set(p.id, p))
            })
          })
        }
      }
    )

    this.addReaction({
      whenThisChanges: () => ({
        sections: this._sections.models,
        assistants: this.fullAccessAssistants.models,
      }),
      thenRunThisCode: ({ sections, assistants }) => {
        const fullAccessIds = new Set(assistants.map((a) => a.id))

        const limitedAssistants = getCollaboratorAssistants(
          this.repository,
          sections
        )
          .filter((c) => !fullAccessIds.has(c.userId))
          .map(
            ({ userId, sectionIds }): LimitedAccessAssistant => ({
              type: 'limited',
              user: this.repository.userStore.getUser(userId),
              sectionIds,
            })
          )

        this.limitedAccessAssistants.replace(limitedAssistants)
      },
    })
    this.addStream(
      getAllAssistantInvitations(this.repository, {
        instructorUserId: this.impersonationInfo?.userId,
      }),
      (invitations) => {
        const sortedInvitations = invitations.sort((a, b) => {
          const aTime = a.data.updatedAt?.getTime() || 0
          const bTime = b.data.updatedAt?.getTime() || 0
          return bTime - aTime
        })
        this.allInvitations.replaceModels(sortedInvitations)
      }
    )
  }

  @computed
  get selectedAssistant() {
    return guardOn<FullAccessAssistant | LimitedAccessAssistant | undefined>(
      this.fullAccessAssistants.isLoaded && this._sections.isLoaded,
      () => {
        const fullAccessAssistant = this.fullAccessAssistants.models.find(
          (a) => a.id === this.assistantId
        )
        if (fullAccessAssistant) {
          return { type: 'full', user: fullAccessAssistant }
        }
        return this.limitedAccessAssistants.find(
          (a) => a.user.id === this.assistantId
        )
      }
    )
  }

  @computed
  get selectedInvitation() {
    return guardOnLoaded(
      this.nonExpiredInvitations,
      (nonExpiredInvitations) => {
        return nonExpiredInvitations.find((i) => i.id === this.invitationId)
      }
    )
  }

  @computed
  get nonExpiredInvitations() {
    return guardOn(this.allInvitations.isLoaded, () => {
      return this.allInvitations.models.filter(
        (i) =>
          !(
            i.data.expiresAt &&
            DateTime.fromJSDate(i.data.expiresAt) <
              this.repository.currentMinute
          )
      )
    })
  }

  @computed
  get sections() {
    return guardOn(this._sections.isLoaded, () => this._sections.models)
  }

  @computed
  get sectionsWithStatusAndAssignments() {
    const isLoaded =
      this._sections.isLoaded &&
      this._sections.models.every(
        (section) => this.assignmentsForSection.get(section.id)?.isLoaded
      ) &&
      this._sections.models.every(
        (section) =>
          !section.data.organizationId ||
          this.invoiceRequestsBySectionId.get(section.id)?.isLoaded
      )

    return guardOn(isLoaded, () => {
      const sections: SectionWithStatusAndAssignments[] =
        this._sections.models.map((section) => {
          const assignments = this.assignmentsForSection.get(section.id)!.models
          const latestInvoiceRequest =
            this.invoiceRequestsBySectionId.get(section.id)?.models[0] ?? null

          const sectionStatus = section.getSectionStatus({
            assignments,
            latestInvoiceRequest,
          })

          return {
            section,
            sectionStatus,
            assignments,
          }
        })

      return sections
    })
  }

  @action
  startAssignmentStream(section: Section, retryCount = 0) {
    const name = `section-assignments-${section.id}`
    if (this.hasStream(name)) return
    this.assignmentsForSection.set(
      section.id,
      SectionAssignment.emptyCollection(this.repository)
    )
    this.addStream(
      getSectionAssignments(this.repository, { sectionId: section.id }),
      (assignments) => {
        this.assignmentsForSection.get(section.id)?.replaceModels(assignments)
      },
      {
        name,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startAssignmentStream(section, retryCount + 1)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  @action
  startSectionRequestStream(section: Section, retryCount = 0) {
    const name = `section-requests-${section.id}`
    if (this.hasStream(name)) return
    this.invoiceRequestsBySectionId.set(
      section.id,
      SectionRequest.emptyCollection(this.repository)
    )
    this.addStream(
      getSectionRequestsBySectionId(this.repository, section.id),
      (requests) => {
        const sortedRequests = requests.sort((a, b) => {
          return b.data.requestedAt.getTime() - a.data.requestedAt.getTime()
        })

        const invoiceRequests = sortedRequests.filter(
          (request) =>
            request.data.sectionRequestType === SectionRequestType.invoice
        )

        this.invoiceRequestsBySectionId
          .get(section.id)!
          .replaceModels(invoiceRequests)
      },
      {
        name,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionRequestStream(section, retryCount + 1)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  async createAssistantInvitation(limitedAccessSectionIds?: string[]) {
    const doc = await createTaInvitation(this.repository, {
      instructorUserId: this.impersonationInfo?.userId,
      collaboratorRWSectionIds: limitedAccessSectionIds,
    })

    return doc.id
  }

  async removeFullAccessAssistant(userId: string) {
    return deleteAssistantship(this.repository.firestore, {
      instructorUserId: this.impersonationInfo?.userId || this.repository.uid,
      userId: userId,
    })
  }

  async removeAssistant(assistant: PublicUser, sectionIds?: string[]) {
    const isFullAccess = this.fullAccessAssistants.models.some(
      (a) => a.id === assistant.id
    )

    if (isFullAccess) {
      await this.removeFullAccessAssistant(assistant.id)
    }

    if (sectionIds && sectionIds.length > 0) {
      const batchUpdates = sectionIds.map((sectionId) => ({
        sectionId,
        assistantUserId: assistant.id,
        action: 'remove' as const,
      }))
      await batchUpdateSectionCollaborators(this.repository, batchUpdates)
    }
  }

  async removeInvitation(invitationId: string) {
    return deleteInvitation(this.repository, invitationId)
  }

  async createTARecord(assistantUserId: string) {
    return createAssistantship(this.repository.firestore, {
      userId: assistantUserId,
      instructorUserId: this.impersonationInfo?.userId || this.repository.uid,
    })
  }

  async updateSectionCollaborators(
    sectionId: string,
    assistantUserId: string,
    action: 'add' | 'remove'
  ) {
    return updateSectionCollaborators(
      this.repository,
      sectionId,
      assistantUserId,
      action
    )
  }

  async updateInvitationSections(
    invitationId: string,
    sectionIds: string[] | null
  ) {
    return updateInvitationSections(this.repository, invitationId, sectionIds)
  }

  async updateAssistantAccess(
    assistantUserId: string,
    accessLevel: AccessLevel,
    sectionIdsToAdd: string[],
    sectionIdsToRemove: string[]
  ) {
    const writes: Promise<unknown>[] = []

    const currentlyHasFullAccess = this.fullAccessAssistants.models.some(
      (a) => a.id === assistantUserId
    )

    if (accessLevel === 'full') {
      if (!currentlyHasFullAccess) {
        writes.push(this.createTARecord(assistantUserId))

        const currentSections =
          this.limitedAccessAssistants.find(
            (a) => a.user.id === assistantUserId
          )?.sectionIds ?? []

        if (currentSections.length > 0) {
          const batchUpdates = currentSections.map((sectionId) => ({
            sectionId,
            assistantUserId,
            action: 'remove' as const,
          }))
          writes.push(
            batchUpdateSectionCollaborators(this.repository, batchUpdates)
          )
        }
      }
    } else {
      if (currentlyHasFullAccess) {
        writes.push(this.removeFullAccessAssistant(assistantUserId))
      }

      const batchUpdates: Array<{
        sectionId: string
        assistantUserId: string
        action: 'add' | 'remove'
      }> = []

      for (const sectionId of sectionIdsToAdd) {
        batchUpdates.push({
          sectionId,
          assistantUserId,
          action: 'add',
        })
      }

      for (const sectionId of sectionIdsToRemove) {
        batchUpdates.push({
          sectionId,
          assistantUserId,
          action: 'remove',
        })
      }

      if (batchUpdates.length > 0) {
        writes.push(
          batchUpdateSectionCollaborators(this.repository, batchUpdates)
        )
      }
    }

    return Promise.allSettled(writes)
  }

  async updateInvitationAccess(
    invitationId: string,
    accessLevel: AccessLevel,
    sectionIds: string[]
  ) {
    const sectionIdsForInvitation = accessLevel === 'full' ? null : sectionIds
    return this.updateInvitationSections(invitationId, sectionIdsForInvitation)
  }
}
